/**
 * This will serve as a service middleware for all reservation configuration calls and its params
 */

import React, {useReducer, useCallback, useMemo, useState} from 'react'

import getUsersReducer from '../../state/user/get-user/reducer'
import {getUser} from '../../state/user/get-user/actions'

import getUserTemplatesReducer from '../../state/user/get-user-templates/reducer'
import {getSearchTemplate} from '../../state/user/get-user-templates/actions'

import getGroupReducer from '../../state/groups/get-group/reducer'
import {getGroup} from '../../state/groups/get-group/actions'

import getGroupFieldsReducer from '../../state/groups/get-group-fields/reducer'
import {getGroupFields} from '../../state/groups/get-group-fields/actions'

import getGroupFieldValuesReducer from '../../state/groups/get-group-field-values/reducer'
import {getGroupFieldValues} from '../../state/groups/get-group-field-values/actions'

import getGroupActionsReducer from '../../state/groups/get-group-actions/reducer'
import {getGroupActions} from '../../state/groups/get-group-actions/actions'

import getGroupActionFieldsReducer from '../../state/groups/get-group-action-fields/reducer'
import {getGroupActionFields} from '../../state/groups/get-group-action-fields/actions'

import usePostConfigurationReducer from '../../state/reservation/post-configuration/reducer'
import {postConfiguration} from '../../state/reservation/post-configuration/actions'

import useGetConfigurationReducer from '../../state/reservation/get-configuration/reducer'
import {getConfiguration} from '../../state/reservation/get-configuration/actions'

import getGroupFiltersReducer from '../../state/groups/get-group-filters/reducer'
import {getGroupFilters} from '../../state/groups/get-group-filters/actions'

import useGetGroupFilterValuesReducer from '../../state/groups/get-group-filter-values/reducer'
import {getGroupFilterValues} from '../../state/groups/get-group-filter-values/actions'

import getAdditionalFieldValuesReducer from '../../state/groups/get-additional-field-values/reducer'
import {getAdditionalFieldValues} from '../../state/groups/get-additional-field-values/actions'

import getEntitiesReducer from '../../state/entities/get-entities/reducer'
import {getEntities} from '../../state/entities/get-entities/actions'

import {getEntityObjects} from '../../state/entities/get-entity-objects-v2/actions'
import useGetEntityObjectsReducer, {
  ActionTypes as getReservationEntityItemsActionTypes,
} from '../../state/entities/get-entity-objects-v2/reducer'
import {getFilterFieldValues} from '../../state/reservation/filter-field-values/actions'
import usefilterFieldValuesReducer from '../../state/reservation/filter-field-values/reducer'

const ReservationConfigurationContext = React.createContext()

function ReservationConfigurationContextProvider(props) {
  // A
  const userState = {
    users: [],
    error: null,
    isLoading: false,
  }

  const templateUserState = {
    error: null,
    isLoading: true,
    userTemplates: [],
  }
  const defaultState = {
    error: null,
    isLoading: true,
  }

  // B
  // get-user
  const [users, userDispatch] = useReducer(getUsersReducer, userState)
  const getAllUsers = useCallback((params = {q: null}, forceRequest = false) => {
    getUser(userDispatch)
  }, [])

  const [multipleListFormDirty, setMultipleListFormDirty] = useState(false)

  // get-group
  const [group, groupDispatch] = useReducer(getGroupReducer, {
    data: {},
    error: null,
    isLoading: false,
  })
  const _getGroup = useCallback(
    ({group_id}) =>
      getGroup(groupDispatch, {
        group_id,
        params: {
          include: ['statuses'].join(','),
        },
      }),
    []
  )

  //get entity objects
  const [entityObjects, entityObjectsDispatch] = useGetEntityObjectsReducer()

  const loadEntityObjects = useCallback(
    (group_id, params = {}) => {
      getEntityObjects(entityObjectsDispatch, group_id, params)
    },
    [entityObjectsDispatch]
  )

  // get-user_templates
  const [userTemplates, templateUsersDispatch] = useReducer(
    getUserTemplatesReducer,
    templateUserState
  )
  const getAllUserTemplates = useCallback(() => getSearchTemplate('', templateUsersDispatch), [])

  // get-group-fields
  const [groupFields, groupFieldsDispatch] = useReducer(getGroupFieldsReducer, {
    error: null,
    isLoading: true,
    data: [],
  })
  const _getGroupFields = useCallback(
    ({group_id, all_types = false}) =>
      getGroupFields(groupFieldsDispatch, {
        group_id,
        type: all_types
          ? []
          : [
              'Group List',
              'ListField', //tested
              'Multiple Group List',
              'MultipleListField', //tested
              'Status',
            ].join(','),
      }),
    []
  )

  // get-group-field-values
  const [groupFieldValues, groupFieldValuesDispatch] = useReducer(getGroupFieldValuesReducer, {
    error: null,
    isLoading: true,
    data: {},
  })
  const _getGroupFieldValues = useCallback(
    ({group_id, field_id}) =>
      getGroupFieldValues(groupFieldValuesDispatch, {
        group_id,
        field_id,
      }),
    []
  )

  // get-group-actions
  const [groupActions, groupActionsDispatch] = useReducer(getGroupActionsReducer, {
    error: null,
    isLoading: false,
    data: [],
  })
  const _getGroupActions = useCallback(
    ({group_id, params}) =>
      getGroupActions(groupActionsDispatch, {
        group_id,
        params,
      }),
    []
  )

  // get-group-action-fields
  const [groupActionFields, groupActionFieldsDispatch] = useReducer(getGroupActionFieldsReducer, {
    error: null,
    isLoading: true,
    data: {},
  })
  const _getGroupActionFields = useCallback(
    ({group_id, action_id, action_types = false, all_fields = false}) =>
      getGroupActionFields(groupActionFieldsDispatch, {
        group_id,
        action_id,
        type: action_types ? ['EntityListField', 'ApUserField'].join(',') : [],
        all_fields: all_fields,
      }),
    []
  )

  // post-configuration
  const [pconfiguration, postConfigurationDispatch] = usePostConfigurationReducer()
  const _postConfiguration = data => postConfiguration(postConfigurationDispatch, data)

  const [filterFieldValues, filterFieldValuesDispatch] = usefilterFieldValuesReducer()
  // get-configuration
  const [gconfiguration, getConfigurationDispatch] = useGetConfigurationReducer()
  const _getConfiguration = useMemo(() => {
    return ({group_id}) =>
      getConfiguration(getConfigurationDispatch, {
        group_id,
      })
  }, [getConfigurationDispatch])

  // get-group-filters
  const [filters, getGroupFiltersDispatch] = useReducer(getGroupFiltersReducer, {
    error: null,
    isLoading: true,
    data: [],
  })
  const _getGroupFilters = useCallback(
    ({group_id, query = null}) =>
      getGroupFilters(getGroupFiltersDispatch, {
        group_id,
        query,
        limit: 50,
        type: ['EntityListField', 'ListField', 'MultipleEntityListField', 'MultipleListField'].join(
          ','
        ),
      }),
    []
  )

  // get-group-filters
  const [filterValues, getGroupFilterValuesDispatch] = useGetGroupFilterValuesReducer()
  const _getGroupFilterValues = useCallback(
    ({group_id, field_id, query = null}) =>
      getGroupFilterValues(getGroupFilterValuesDispatch, {
        group_id,
        query,
        limit: 50,
        field_id,
      }),
    [getGroupFilterValuesDispatch]
  )

  // get-additional-field values
  const [additionalFieldValues, getAdditionalFieldValuesDispatch] = useReducer(
    getAdditionalFieldValuesReducer,
    {
      error: null,
      isLoading: true,
      data: {},
    }
  )
  const _getAdditionalFieldValues = useCallback(
    (group_id, field_types, include_actions, include_related, currency_items) =>
      getAdditionalFieldValues(
        getAdditionalFieldValuesDispatch,
        group_id,
        field_types,
        include_actions,
        include_related,
        currency_items
      ),
    []
  )

  // get-all-entities
  const [groups, getGroupsDispatch] = useReducer(getEntitiesReducer, {
    error: null,
    isLoading: true,
    data: [],
  })
  const _getGroups = useCallback(() => getEntities(getGroupsDispatch), [])

  // get reserved for data
  const loadFilterFieldValues = useCallback(
    async (field_id, search, params) => {
      const res = await getFilterFieldValues(filterFieldValuesDispatch, field_id, search, params)
      return res
    },
    [filterFieldValuesDispatch]
  )
  // C
  const value = {
    users,
    getAllUsers,

    userTemplates,
    getAllUserTemplates,

    group,
    getGroup: _getGroup,

    groupFields,
    getGroupFields: _getGroupFields,

    groupFieldValues,
    getGroupFieldValues: _getGroupFieldValues,

    groupActions,
    getGroupActions: _getGroupActions,

    groupActionFields,
    getGroupActionFields: _getGroupActionFields,

    pconfiguration,
    postConfiguration: _postConfiguration,

    gconfiguration,
    getConfiguration: _getConfiguration,

    filters,
    getGroupFilters: _getGroupFilters,

    filterValues,
    getGroupFilterValues: _getGroupFilterValues,

    additionalFieldValues,
    getAdditionalFieldValues: _getAdditionalFieldValues,

    groups,
    getGroups: _getGroups,

    entityObjects,
    loadEntityObjects,
    setMultipleListFormDirty,
    multipleListFormDirty,
    filterFieldValues,
    loadFilterFieldValues,
  }

  return (
    <ReservationConfigurationContext.Provider value={value}>
      {props.children}
    </ReservationConfigurationContext.Provider>
  )
}

const ReservationConfigurationContextConsumer = ReservationConfigurationContext.Consumer

export {
  ReservationConfigurationContext,
  ReservationConfigurationContextProvider,
  ReservationConfigurationContextConsumer,
}
